import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DataSourceMode } from '../services/DataBridge';

interface GuidedDemoProps {
  open: boolean;
  activeTab: string;
  dataSourceMode: DataSourceMode;
  onOpenChange: (open: boolean) => void;
  onNavigateTab: (tab: string) => void;
  onDataSourceModeChange: (mode: DataSourceMode) => void | Promise<void>;
}

interface GuidedDemoStep {
  tab: string;
  icon: string;
  title: string;
  purpose: string;
  checks: string[];
  narration: string;
  seconds: number;
}

const GUIDE_ACTION_KEY = 'aees:energyplus-guide-action';

export const GUIDED_DEMO_STEPS: GuidedDemoStep[] = [
  {
    tab: 'overview',
    icon: 'fa-cubes',
    title: '공간 트윈',
    purpose: '건물의 현재 상태와 모든 분석 화면이 같은 시각의 데이터를 공유하는지 보여줍니다.',
    checks: ['데이터 원천과 관측 시각', '층별 온도·습도 및 운전상태', '3D 공간과 센서 포인트 연결'],
    narration: '먼저 EnergyPlus 가상 BEMS의 현재 상태가 건물 공간과 어떻게 연결되는지 확인합니다.',
    seconds: 45,
  },
  {
    tab: 'digital-twin',
    icon: 'fa-wave-square',
    title: '트윈 분석',
    purpose: '가상 BEMS 관측값과 Tracking 예측값이 시간에 맞춰 비교되는 과정을 보여줍니다.',
    checks: ['실측·예측 곡선 갱신', 'cvRMSE·MAE와 데이터 품질', '자동 보정 후보와 적용 상태'],
    narration: 'Tracking은 같은 시각의 관측값과 예측값을 비교하고 오차가 기준을 벗어나면 보정 근거를 남깁니다.',
    seconds: 55,
  },
  {
    tab: 'loop-water',
    icon: 'fa-droplet',
    title: '열원 계통',
    purpose: '설정에서 작성한 열원 토폴로지가 실제 운영 계통 화면에 동일하게 투영됨을 보여줍니다.',
    checks: ['냉방·난방 운전모드', '공급·환수·바이패스 배관', '설비 ON/OFF와 흐름 애니메이션'],
    narration: '열원, 펌프, 버퍼탱크와 부하 사이의 공급·환수 흐름을 운전모드별로 확인할 수 있습니다.',
    seconds: 45,
  },
  {
    tab: 'loop-air',
    icon: 'fa-wind',
    title: '공조 계통',
    purpose: 'AHU·VAV·TABS·Convector와 실내 공간의 연결 및 운전상태를 보여줍니다.',
    checks: ['층별 VAV와 실내공간 연결', '냉방·난방 공조 흐름', '온도·습도·ΔT 그래프'],
    narration: '공조 계통은 열원측 상태와 같은 시각을 사용하며 각 층의 말단 공간까지 연결됩니다.',
    seconds: 45,
  },
  {
    tab: 'ai-control',
    icon: 'fa-sliders',
    title: 'AI 제어 분석',
    purpose: 'MASAC 정책이 현재 상태를 바탕으로 제어값을 결정하고 안전범위를 지키는지 보여줍니다.',
    checks: ['수동·자동·제안 모드', '시스템 ON 조건과 액션 범위', 'Q값·NAM 기여도 및 Baseline 비교'],
    narration: 'AI 제어는 시스템이 운전 중일 때만 계산되며 물리적 제약을 통과한 값만 EnergyPlus에 반영됩니다.',
    seconds: 55,
  },
  {
    tab: 'ai-model',
    icon: 'fa-brain',
    title: '정책 튜닝',
    purpose: '운영 정책과 격리된 후보 Fine-tuning 및 검증·승격 절차를 보여줍니다.',
    checks: ['현재 배포 정책과 후보 정책 분리', '검증된 Calibration Package', '검증 후 적용 승인 절차'],
    narration: '후보 학습 중에는 현재 BEMS 운영 정책이 바뀌지 않으며 검증을 통과한 모델만 별도로 적용합니다.',
    seconds: 40,
  },
  {
    tab: 'reporting',
    icon: 'fa-file-shield',
    title: '운영 리포트',
    purpose: '운전 이력, Tracking 품질, 에너지·쾌적성 성과를 보고서로 저장하는 기능을 보여줍니다.',
    checks: ['Baseline 대비 에너지 절감', '층별 온·습도와 쾌적성', 'CSV 및 현장 보고서 저장'],
    narration: '실시간 시연 결과와 장기 EnergyPlus 평가 결과의 범위를 구분하여 보고서 근거와 함께 제공합니다.',
    seconds: 50,
  },
  {
    tab: 'settings',
    icon: 'fa-gears',
    title: '설정·범용 시설 구축',
    purpose: '다른 공공시설의 IDF·센서·BIM·토폴로지를 단계적으로 구성하는 기능을 보여줍니다.',
    checks: ['시설 구축 스튜디오 단계', 'IDF 자동분석과 통합 매핑', '토폴로지 검증 후 새 버전 적용'],
    narration: '서울에너지드림센터에 고정된 화면이 아니라 시설 프로필과 매핑을 바꿔 다른 건물로 확장할 수 있습니다.',
    seconds: 60,
  },
];

export const GuidedDemo: React.FC<GuidedDemoProps> = ({
  open,
  activeTab,
  dataSourceMode,
  onOpenChange,
  onNavigateTab,
  onDataSourceModeChange,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(GUIDED_DEMO_STEPS[0].seconds);
  const [sessionState, setSessionState] = useState<'idle' | 'preparing' | 'requested' | 'error'>('idle');
  const [sessionError, setSessionError] = useState<string | null>(null);
  const wasOpenRef = useRef(false);
  const step = GUIDED_DEMO_STEPS[stepIndex];
  const isLastStep = stepIndex === GUIDED_DEMO_STEPS.length - 1;

  const moveToStep = useCallback((nextIndex: number) => {
    const bounded = Math.max(0, Math.min(GUIDED_DEMO_STEPS.length - 1, nextIndex));
    setStepIndex(bounded);
    setRemainingSeconds(GUIDED_DEMO_STEPS[bounded].seconds);
    onNavigateTab(GUIDED_DEMO_STEPS[bounded].tab);
  }, [onNavigateTab]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setStepIndex(0);
      setAutoPlay(false);
      setCollapsed(false);
      setRemainingSeconds(GUIDED_DEMO_STEPS[0].seconds);
      setSessionState('idle');
      setSessionError(null);
      onNavigateTab(GUIDED_DEMO_STEPS[0].tab);
    }
    wasOpenRef.current = open;
  }, [onNavigateTab, open]);

  useEffect(() => {
    if (!open || !autoPlay) return undefined;
    const timer = window.setInterval(() => {
      setRemainingSeconds(previous => {
        if (previous > 1) return previous - 1;
        if (isLastStep) {
          setAutoPlay(false);
          return 0;
        }
        window.setTimeout(() => moveToStep(stepIndex + 1), 0);
        return 0;
      });
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [autoPlay, isLastStep, moveToStep, open, stepIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches('input, textarea, select, button, a, [contenteditable="true"]')) return;
      if (event.key === 'ArrowRight' && !isLastStep) moveToStep(stepIndex + 1);
      if (event.key === 'ArrowLeft' && stepIndex > 0) moveToStep(stepIndex - 1);
      if (event.key === ' ') {
        event.preventDefault();
        setAutoPlay(value => !value);
      }
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLastStep, moveToStep, onOpenChange, open, stepIndex]);

  const startEnergyPlusDemo = useCallback(async () => {
    if (sessionState === 'preparing' || sessionState === 'requested') return;
    setSessionState('preparing');
    setSessionError(null);
    try {
      await onDataSourceModeChange('energyplus');
      const request = {
        id: `guided-demo-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        mode: 'demo',
        requestedAt: Date.now(),
      };
      try { window.sessionStorage.setItem(GUIDE_ACTION_KEY, JSON.stringify(request)); } catch {}
      setSessionState('requested');
      moveToStep(1);
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('aees:energyplus-guide-action', { detail: request }));
      }, 600);
    } catch (error) {
      setSessionState('error');
      setSessionError(error instanceof Error ? error.message : 'EnergyPlus 시연을 준비하지 못했습니다.');
    }
  }, [moveToStep, onDataSourceModeChange, sessionState]);

  const progress = useMemo(() => ((stepIndex + 1) / GUIDED_DEMO_STEPS.length) * 100, [stepIndex]);

  if (!open) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="fixed right-4 top-4 z-[260] flex items-center gap-2 rounded-full border border-[#8fc7c3] bg-white px-3 py-2 text-[10px] font-semibold text-[#08777d] shadow-[0_12px_32px_rgba(31,39,45,0.18)]"
        aria-label="시연 가이드 펼치기"
      >
        <i className={`fa-solid ${step.icon}`} />
        시연 {stepIndex + 1}/{GUIDED_DEMO_STEPS.length} · {step.title}
        {autoPlay && <span className="font-mono text-[#7a848c]">{remainingSeconds}s</span>}
      </button>
    );
  }

  return (
    <aside className="fixed right-4 top-4 z-[260] w-[min(390px,calc(100vw-32px))] overflow-hidden rounded-[22px] border border-[#b9d8d4] bg-white/95 shadow-[0_20px_52px_rgba(31,39,45,0.2)] backdrop-blur-xl" aria-label="AEES BEMS 순차 시연 가이드">
      <div className="h-1 bg-[#e1e8e7]">
        <div className="h-full bg-[#0f8b8d] transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#e7f5f3] text-[#08777d]"><i className={`fa-solid ${step.icon}`} /></span>
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#08777d]">Guided Demonstration · {stepIndex + 1}/{GUIDED_DEMO_STEPS.length}</p>
              <h2 className="mt-0.5 text-sm font-bold text-[#20272d]">{step.title}</h2>
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <button type="button" onClick={() => setCollapsed(true)} className="grid h-7 w-7 place-items-center rounded-lg text-[#7a848c] hover:bg-black/5" aria-label="시연 가이드 접기"><i className="fa-solid fa-minus" /></button>
            <button type="button" onClick={() => onOpenChange(false)} className="grid h-7 w-7 place-items-center rounded-lg text-[#7a848c] hover:bg-black/5" aria-label="시연 가이드 종료"><i className="fa-solid fa-xmark" /></button>
          </div>
        </div>

        <p className="mt-3 text-[11px] leading-[1.65] text-[#4f5d65]">{step.purpose}</p>
        <div className="mt-3 rounded-xl border border-[#dce5e4] bg-[#f6faf9] p-3">
          <p className="text-[9px] font-semibold text-[#66717a]">이 화면에서 확인할 기능</p>
          <ul className="mt-2 space-y-1.5">
            {step.checks.map(check => (
              <li key={check} className="flex items-start gap-2 text-[10px] leading-4 text-[#37444b]">
                <i className="fa-solid fa-circle-check mt-0.5 text-[9px] text-[#2d9b88]" />
                <span>{check}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-2.5 rounded-xl border border-[#ead7ae] bg-[#fff9ee] px-3 py-2.5">
          <p className="text-[9px] font-semibold text-[#946012]"><i className="fa-solid fa-microphone-lines mr-1.5" />발표 설명</p>
          <p className="mt-1 text-[10px] leading-4 text-[#6f5b35]">{step.narration}</p>
        </div>

        {stepIndex === 0 && sessionState !== 'requested' && (
          <button
            type="button"
            disabled={sessionState === 'preparing'}
            onClick={() => void startEnergyPlusDemo()}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#08777d] px-3 py-2.5 text-[10px] font-semibold text-white hover:bg-[#06686d] disabled:cursor-wait disabled:opacity-55"
          >
            <i className={`fa-solid ${sessionState === 'preparing' ? 'fa-circle-notch fa-spin' : 'fa-play'}`} />
            {sessionState === 'preparing' ? 'EnergyPlus 시연 준비 중' : 'EnergyPlus·Tracking·RL 시연 데이터 시작'}
          </button>
        )}
        {sessionError && <p className="mt-2 rounded-lg bg-[#fff1f1] px-2.5 py-2 text-[9px] text-[#a34d4d]">{sessionError}</p>}

        <div className="mt-3 flex items-center justify-between gap-2 border-t border-[#e2e6e7] pt-3">
          <button
            type="button"
            disabled={stepIndex === 0}
            onClick={() => moveToStep(stepIndex - 1)}
            className="h-9 rounded-xl border border-[#d7dddf] bg-white px-3 text-[10px] font-semibold text-[#66717a] disabled:opacity-35"
          >
            <i className="fa-solid fa-chevron-left mr-1.5" />이전
          </button>
          <button
            type="button"
            onClick={() => setAutoPlay(value => !value)}
            className={`h-9 rounded-xl border px-3 text-[10px] font-semibold ${autoPlay ? 'border-[#e1bd76] bg-[#fff8ea] text-[#8a5d13]' : 'border-[#b9d8d4] bg-[#f3faf9] text-[#08777d]'}`}
            title="각 화면을 권장 설명시간 후 자동으로 넘깁니다."
          >
            <i className={`fa-solid ${autoPlay ? 'fa-pause' : 'fa-forward'} mr-1.5`} />
            {autoPlay ? `일시정지 · ${remainingSeconds}s` : '자동 진행'}
          </button>
          {isLastStep ? (
            <button type="button" onClick={() => onOpenChange(false)} className="h-9 rounded-xl bg-[#20272d] px-3 text-[10px] font-semibold text-white">시연 종료<i className="fa-solid fa-check ml-1.5" /></button>
          ) : (
            <button type="button" onClick={() => moveToStep(stepIndex + 1)} className="h-9 rounded-xl bg-[#0f8b8d] px-3 text-[10px] font-semibold text-white">다음<i className="fa-solid fa-chevron-right ml-1.5" /></button>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between text-[8px] text-[#8a949b]">
          <span>←/→ 단계 이동 · Space 자동재생 · Esc 종료</span>
          <span className="font-medium">{activeTab === step.tab ? '화면 열림' : '화면 이동 중'} · {dataSourceMode === 'energyplus' ? 'EnergyPlus' : '데이터 전환 전'}</span>
        </div>
      </div>
    </aside>
  );
};
